import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, RotateCcw, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status, turnsUsed, maxTurns }) => {
  const remaining = Math.max(0, maxTurns - turnsUsed);
  const map = {
    active: {
      label: `Actief · ${remaining} berichten over`,
      cls: "bg-[#EAF1E8] text-[#3D4D41] border-[#C9D7C5]",
    },
    done: {
      label: "Sessie afgerond",
      cls: "bg-[#F5EAE2] text-[#B37A5F] border-[#E5D2C2]",
    },
    limit_reached: {
      label: "Berichtenlimiet bereikt",
      cls: "bg-[#F5EAE2] text-[#B37A5F] border-[#E5D2C2]",
    },
  };
  const t = map[status] || map.active;
  return (
    <span
      data-testid="got-status-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] border ${t.cls}`}
    >
      {t.label}
    </span>
  );
};

const Bubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`got-message-${msg.role}`}
    >
      <div
        className={`h-9 w-9 rounded-full shrink-0 flex items-center justify-center border ${
          isUser
            ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
            : "bg-[#F5EAE2] text-[#B37A5F] border-[#E5D2C2]"
        }`}
        aria-hidden
      >
        {isUser ? (
          <UserIcon strokeWidth={1.5} size={16} />
        ) : (
          <Bot strokeWidth={1.5} size={16} />
        )}
      </div>
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed ${
          isUser
            ? "bg-[#4A5D4E] text-white rounded-tr-sm whitespace-pre-wrap"
            : "bg-white text-[#1A1D1A] border border-[#E5E2DC] rounded-tl-sm got-markdown"
        }`}
      >
        {isUser ? (
          msg.content
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Render Markdown without injecting a wrapping <p> that would break our
              // tight bubble layout, and keep links safe.
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#4A5D4E] hover:text-[#3D4D41]"
                />
              ),
            }}
          >
            {msg.content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

export default function GotPractice() {
  const { t } = useT();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [session?.messages?.length, sending]);

  const startSession = async () => {
    setLoading(true);
    setSession(null);
    setDraft("");
    try {
      const { data } = await api.post("/got/sessions");
      setSession(data);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Kon de oefensessie niet starten."
      );
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const content = draft.trim();
    if (!content || sending || !session || session.status !== "active") return;
    setSending(true);
    // Optimistic UI: append the user's bubble immediately
    const optimistic = {
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setSession({ ...session, messages: [...session.messages, optimistic] });
    setDraft("");
    try {
      const { data } = await api.post(
        `/got/sessions/${session.id}/message`,
        { content }
      );
      setSession(data);
    } catch (err) {
      // Revert optimistic update on failure
      setSession((s) =>
        s ? { ...s, messages: s.messages.slice(0, -1) } : s
      );
      setDraft(content);
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Het bericht kon niet worden verstuurd."
      );
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const canSend =
    !!session && session.status === "active" && !sending && draft.trim().length > 0;
  const isClosed = session && session.status !== "active";

  return (
    <div
      className="px-8 lg:px-12 py-12 max-w-3xl mx-auto"
      data-testid="got-practice-page"
    >
      <PageHeader
        overline={t("got.overline")}
        title={t("got.title")}
        description={t("got.description")}
      />

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        {session ? (
          <StatusBadge
            status={session.status}
            turnsUsed={session.turns_used}
            maxTurns={session.max_turns}
          />
        ) : (
          <span />
        )}
        <Button
          onClick={startSession}
          disabled={loading}
          data-testid="got-new-session-button"
          variant="outline"
          className="h-9 rounded-lg border-[#E5E2DC] text-[#1A1D1A] hover:bg-[#F2F0EA]"
        >
          <RotateCcw strokeWidth={1.5} size={14} className="mr-1.5" />
          Nieuwe sessie
        </Button>
      </div>

      {/* Transcript */}
      <div
        className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-2xl p-5 sm:p-6 min-h-[420px] max-h-[60vh] overflow-y-auto space-y-4"
        data-testid="got-transcript"
      >
        {loading && !session ? (
          <div className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold flex items-center gap-2">
            <Sparkles strokeWidth={1.5} size={14} className="text-[#B37A5F]" />
            De AI bereidt je oefensessie voor…
          </div>
        ) : (
          <>
            {session?.messages?.map((m, idx) => (
              <Bubble key={`${m.created_at}-${idx}`} msg={m} />
            ))}
            {sending && (
              <div className="flex gap-3" data-testid="got-typing-indicator">
                <div className="h-9 w-9 rounded-full bg-[#F5EAE2] border border-[#E5D2C2] flex items-center justify-center text-[#B37A5F]">
                  <Bot strokeWidth={1.5} size={16} />
                </div>
                <div className="px-4 py-3 rounded-2xl bg-white border border-[#E5E2DC] text-[#8A9188] text-sm italic">
                  aan het typen…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </>
        )}
      </div>

      {/* Composer / closed-state card */}
      {isClosed ? (
        <div
          className="mt-5 bg-[#F5EAE2] border border-[#E5D2C2] rounded-2xl p-5 flex items-start gap-3"
          data-testid="got-closed-card"
        >
          <Sparkles strokeWidth={1.5} className="text-[#B37A5F] shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-sm text-[#1A1D1A] font-medium">
              {session.status === "limit_reached"
                ? "Je hebt het maximum aantal berichten voor deze sessie bereikt."
                : "Goed gedaan — deze oefensessie is afgerond."}
            </p>
            <p className="text-xs text-[#5C635A] mt-1">
              Klik op "Nieuwe sessie" om opnieuw te beginnen.
            </p>
          </div>
          <Button
            onClick={startSession}
            data-testid="got-restart-button"
            className="h-9 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            Nieuwe sessie
          </Button>
        </div>
      ) : (
        <form onSubmit={sendMessage} className="mt-5 flex gap-2" data-testid="got-composer">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            disabled={!session || sending}
            placeholder={
              session
                ? "Typ je antwoord… (Enter om te versturen, Shift+Enter voor een nieuwe regel)"
                : "Sessie wordt voorbereid…"
            }
            rows={2}
            data-testid="got-composer-input"
            className="flex-1 resize-none rounded-lg bg-white border border-[#E5E2DC] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E] px-4 py-3 text-[15px] leading-relaxed"
          />
          <Button
            type="submit"
            disabled={!canSend}
            data-testid="got-send-button"
            className="h-auto self-stretch px-5 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white disabled:opacity-40"
          >
            <Send strokeWidth={1.5} size={16} className="mr-1.5" />
            Versturen
          </Button>
        </form>
      )}
    </div>
  );
}
